// src/components/Navbar.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, X, Search } from "lucide-react";
import HamburgerIcon from "./HamburgerIcon";
import * as cardsModule from "./card-data";

/* Normalize card-data export (array or object) */
function getCardsArray(mod) {
  const candidate =
    mod?.default ?? mod?.cardData ?? mod?.cards ?? mod?.data ?? null;
  if (Array.isArray(candidate)) return candidate;
  if (candidate && typeof candidate === "object") return Object.values(candidate);
  const firstArray = Object.values(mod || {}).find(Array.isArray);
  return Array.isArray(firstArray) ? firstArray : [];
}

/* Build correct card path (kept for compatibility) */
const cardPath = (slug) => (slug?.startsWith("/") ? slug : `/tarot-card/${slug || ""}`);

/* Zodiac list for Tarotscope dropdown/accordion */
const ZODIAC_SIGNS = [
  { name: "Aries", slug: "aries" },
  { name: "Taurus", slug: "taurus" },
  { name: "Gemini", slug: "gemini" },
  { name: "Cancer", slug: "cancer" },
  { name: "Leo", slug: "leo" },
  { name: "Virgo", slug: "virgo" },
  { name: "Libra", slug: "libra" },
  { name: "Scorpio", slug: "scorpio" },
  { name: "Sagittarius", slug: "sagittarius" },
  { name: "Capricorn", slug: "capricorn" },
  { name: "Aquarius", slug: "aquarius" },
  { name: "Pisces", slug: "pisces" },
];

export default function Navbar() {
  const [searchOpen, setSearchOpen] = useState(false);
  const searchFormRef = useRef(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const [showMobileEmotions, setShowMobileEmotions] = useState(false);
  const [showMobileSiteInfo, setShowMobileSiteInfo] = useState(false);
  const [showMobileCards, setShowMobileCards] = useState(false);
  const [showMobileTarotscope, setShowMobileTarotscope] = useState(false); // NEW

  // Desktop dropdowns
  const [showEmotions, setShowEmotions] = useState(false);
  const [showSiteInfo, setShowSiteInfo] = useState(false);
  const [showCards, setShowCards] = useState(false);
  const [showTarotscope, setShowTarotscope] = useState(false); // NEW

  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isFocused, setIsFocused] = useState(false);

  const navigate = useNavigate();
  const menuRef = useRef(null);
  const emotionsTimeout = useRef(null);
  const siteInfoTimeout = useRef(null);
  const cardsTimeout = useRef(null);
  const tarotscopeTimeout = useRef(null); // NEW
  const desktopSearchRef = useRef(null);

  const cards = useMemo(() => getCardsArray(cardsModule), []);

  // Submit search (both desktop and mobile)
  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    navigate(`/search?query=${encodeURIComponent(q)}`);
    setSearchQuery("");
    setSuggestions([]);
  };

  const handleSubmitSearch = (term) => {
    if (term.trim()) {
      navigate(`/search?query=${encodeURIComponent(term)}`);
      setShowMobileMenu(false);
    }
  };

  // Navigate and clean up (used by mobile suggestions)
  const goTo = (to) => {
    navigate(to);
    setSearchQuery("");
    setSuggestions([]);
    setShowMobileMenu(false);
  };

  // click away (desktop search)
  useEffect(() => {
    const onPointerDown = (e) => {
      if (!desktopSearchRef.current) return;
      if (!desktopSearchRef.current.contains(e.target)) {
        setIsFocused(false);
        setSearchOpen(false);
        setSuggestions([]);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  // close suggestion box (mobile search)
  const formRef = useRef(null);
  useEffect(() => {
    const onPointerDown = (e) => {
      if (!formRef.current) return;
      if (!formRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = showMobileMenu ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [showMobileMenu]);

  // Close mobile menu when clicking outside panel
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMobileMenu(false);
      }
    };
    if (showMobileMenu) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMobileMenu]);

  // Suggestions while typing (cards only)
  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      setSuggestions([]);
      return;
    }
    const scored = (Array.isArray(cards) ? cards : [])
      .filter((c) => c?.name)
      .map((c) => {
        const name = c.name.toLowerCase();
        let score = 0;
        if (name.startsWith(q)) score += 100;
        if (name.includes(q)) score += 10;
        return { ...c, _score: score };
      })
      .filter((c) => c._score > 0)
      .sort((a, b) => b._score - a._score)
      .slice(0, 8);
    setSuggestions(scored);
  }, [searchQuery, cards]);

  const emotionsList = [
    "Uncertain Love",
    "Heart Broken",
    "Anxious",
    "Confused",
    "Lonely",
    "Hopeless",
    "Guilty",
    "Betrayed",
    "Insecure",
    "Overwhelmed",
    "Other",
  ];

  const tarotSuits = [
    { name: "Major Arcana", slug: "/tarot/major-arcana-tarot-suit-cards-meaning" },
    { name: "Wands", slug: "/tarot/wands-tarot-suit-cards-meaning" },
    { name: "Cups", slug: "/tarot/cups-tarot-suit-cards-meaning" },
    { name: "Swords", slug: "/tarot/swords-tarot-suit-cards-meaning" },
    { name: "Pentacles", slug: "/tarot/pentacles-tarot-suit-cards-meaning" },
    { name: "All Cards", slug: "/tarot-cards" },
  ];

  return (
    <>
      {/* =========================
            DESKTOP NAVBAR (Top Layer)
          ========================= */}
      <nav
        className="
          hidden min-[1050px]:flex
          relative z-[300]
          bg-gradient-to-b from-black/45 via-[#14656a]/25 to-[#0f3d3f]/5 backdrop-blur-2xl
          text-white px-3 py-5 border-b border-white/40
          overflow-x-clip 
        "
      >
        <div className="w-full max-w-screen-xl mx-auto">
          <div className="min-w-0 overflow-x-clip">
            <div
              className="
                flex items-center justify-center
                gap-2 lg:gap-3 xl:gap-3
                flex-wrap max-[1280px]:flex-nowrap
                whitespace-normal max-[1280px]:whitespace-nowrap
                relative
                font-[Montserrat] uppercase tracking-[0.08em] text-[13px] lg:text[13px] font-narrow
              "
            >
              <Link to="/" className="hover:scale-110 transform origin-bottom font-[arial]">
                HOME
              </Link>

              <Link
                to="/what-is-tarot-cards-complete-guide"
                className="hover:scale-110 transform origin-bottom font-[arial]"
              >
                WHAT'S TAROT
              </Link>

              {/* Tarot Cards */}
              <div
                className="relative"
                onMouseEnter={() => {
                  clearTimeout(cardsTimeout.current);
                  setShowCards(true);
                }}
                onMouseLeave={() => {
                  cardsTimeout.current = setTimeout(() => setShowCards(false), 150);
                }}
              >
                <button
                  className="flex items-center gap-1 hover:scale-110 transform origin-bottom font-[arial]"
                  aria-haspopup="true"
                  aria-expanded={showCards ? "true" : "false"}
                >
                  TAROT CARDS <ChevronDown size={18} />
                </button>
                {showCards && (
                  <div
                    className="
                      absolute z-[310]
                      mt-2 min-w-[100px]
                      rounded-none overflow-hidden
                      bg-gradient-to-b from-white/90 via-white/95 to-white/95
                      ring- ring-[#2b9cb3]/80 shadow-lg
                      text-[#0f3d3f] normal-case text-left font-[arial]
                    "
                  >
                    <ul className="divide-y divide-[#2b9cb3]/40">
                      {tarotSuits.map((item) => (
                        <li key={item.slug}>
                          <Link
                            to={item.slug}
                            className="flex w-full items-center gap-2 px-4 py-2 hover:bg-[#0f3d3f]/10 transition"
                          >
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Emotions */}
              <div
                className="relative"
                onMouseEnter={() => {
                  clearTimeout(emotionsTimeout.current);
                  setShowEmotions(true);
                }}
                onMouseLeave={() => {
                  emotionsTimeout.current = setTimeout(() => setShowEmotions(false), 150);
                }}
              >
                <button
                  className="flex items-center gap-1 hover:scale-110 transform origin-bottom font-[arial]"
                  aria-haspopup="true"
                  aria-expanded={showEmotions ? "true" : "false"}
                >
                  EMOTIONS <ChevronDown size={18} />
                </button>
                {showEmotions && (
                  <div
                    className="
                      absolute z-[310]
                      mt-2 min-w-[100px]
                      rounded-none overflow-hidden
                      bg-gradient-to-b from-white/100 via-white/100 to-white/100
                      ring- ring-[#2b9cb3]/50 shadow-lg
                      text-[#0f3d3f]  normal-case text-left font-[arial]
                    "
                  >
                    <ul className="divide-y divide-[#2b9cb3]/40">
                      {emotionsList.map((emotion) => {
                        const slug = emotion.toLowerCase().replace(/ /g, "-");
                        return (
                          <li key={emotion}>
                            <Link
                              to={`/emotions/tarot-reading-for-${slug}`}
                              className="flex w-full items-center gap-2 px-4 py-2 hover:bg-[#0f3d3f]/10 transition"
                            >
                              {emotion}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>

              {/* NEW: Yes/No + Read My Card */}
              <Link to="/yes-no-tarot" className="hover:scale-110 transform origin-bottom font-[arial]">
                YES/NO TAROT
              </Link>
              <Link to="/readmycard" className="hover:scale-110 transform origin-bottom font-[arial]">
                READ MY CARD
              </Link>

              {/* NEW: Tarotscope dropdown (same style as Emotions) */}
              <div
                className="relative"
                onMouseEnter={() => {
                  clearTimeout(tarotscopeTimeout.current);
                  setShowTarotscope(true);
                }}
                onMouseLeave={() => {
                  tarotscopeTimeout.current = setTimeout(() => setShowTarotscope(false), 150);
                }}
              >
                <button
                  className="flex items-center gap-1 hover:scale-110 transform origin-bottom font-[arial]"
                  aria-haspopup="true"
                  aria-expanded={showTarotscope ? "true" : "false"}
                >
                  TAROTSCOPE <ChevronDown size={18} />
                </button>
                {showTarotscope && (
                  <div
                    className="
                      absolute z-[310]
                      mt-2 min-w-[180px]
                      rounded-none overflow-hidden
                      bg-gradient-to-b from-white/100 via-white/100 to-white/100
                      ring- ring-[#2b9cb3]/50 shadow-lg
                      text-[#0f3d3f] normal-case text-left font-[arial]
                    "
                  >
                    <ul className="divide-y divide-[#2b9cb3]/40">
                      {ZODIAC_SIGNS.map((s) => (
                        <li key={s.slug}>
                          <Link
                            to={`/tarotscope/${s.slug}`}
                            className="flex w-full items-center gap-2 px-4 py-2 hover:bg-[#0f3d3f]/10 transition"
                          >
                            {s.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Remains after the new items */}
              <Link to="/fate-friday" className="hover:scale-110 transform origin-bottom font-[arial]">
                FATE FRIDAY
              </Link>

              <Link to="/full-moon" className="hover:scale-110 transform origin-bottom font-[arial]">
                FULL MOON
              </Link>

              {/* This Space */}
              <div
                className="relative"
                onMouseEnter={() => {
                  clearTimeout(siteInfoTimeout.current);
                  setShowSiteInfo(true);
                }}
                onMouseLeave={() => {
                  siteInfoTimeout.current = setTimeout(() => setShowSiteInfo(false), 150);
                }}
              >
                <button
                  className="flex items-center gap-1 hover:scale-110 transform origin-bottom font-[arial]"
                  aria-haspopup="true"
                  aria-expanded={showSiteInfo ? "true" : "false"}
                >
                  THIS SPACE <ChevronDown size={18} />
                </button>
                {showSiteInfo && (
                  <div
                    className="
                      absolute z-[310]
                      mt-2 min-w-[100px]
                      rounded-none overflow-hidden
                      bg-gradient-to-b from-white/100 via-white/100 to-white/100
                      ring- ring-[#2b9cb3]/50 shadow-lg
                      text-[#0f3d3f] normal-case text-left font-[arial]
                    "
                  >
                    <ul className="divide-y divide-[#2b9cb3]/40">
                      <li>
                        <Link
                          to="/about"
                          className="flex w-full items-center gap-2 px-4 py-2 hover:bg-[#0f3d3f]/10 transition "
                        >
                          About
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/disclaimer"
                          className="flex w-full items-center gap-2 px-4 py-2 hover:bg-[#0f3d3f]/10 transition"
                        >
                          Disclaimer
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/privacy"
                          className="flex w-full items-center gap-2 px-4 py-2 hover:bg-[#0f3d3f]/10 transition"
                        >
                          Privacy
                        </Link>
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Inline search (desktop) */}
              <div
                ref={desktopSearchRef}
                className="relative inline-flex items-center ml-2 normal-case tracking-normal font-normal shrink-0"
              >
                <form
                  ref={searchFormRef}
                  onSubmit={handleSearch}
                  className={`flex items-center overflow-hidden transition-all duration-300 ${
                    searchOpen
                      ? "bg-white/50 border border-[#2b9cb3] rounded-full px-2 py-1"
                      : "bg-transparent border-0 p-0"
                  }`}
                  style={{ width: searchOpen ? "14rem" : "2rem", maxWidth: "100%" }}
                  aria-label="Site search"
                  onFocus={() => setIsFocused(true)}
                  onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                      setIsFocused(false);
                      setSearchOpen(false);
                    }
                  }}
                >
                  <button
                    type={searchOpen ? "submit" : "button"}
                    className="text-white p-1"
                    aria-label={searchOpen ? "Submit search" : "Open search"}
                    onClick={(e) => {
                      if (!searchOpen) {
                        e.preventDefault();
                        setSearchOpen(true);
                        setTimeout(() => {
                          const input = searchFormRef.current?.querySelector("input");
                          input?.focus();
                        }, 10);
                      }
                    }}
                  >
                    <Search size={20} />
                  </button>

                  <input 
                    type="text"
                    placeholder="Search Tarot Cards ..."
                    className={`outline-none text-base text-[#0f3d3f] placeholder-[#0f3d3f]/80 bg-transparent transition-all duration-200 ${
                      searchOpen ? "px-2 w-full opacity-100" : "px-0 w-0 opacity-0 text-[#0f3d3f]"
                    }`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        setIsFocused(false);
                        setSearchOpen(false);
                        const el = e.target;
                        if (el && typeof el.blur === "function") el.blur();
                      }
                    }}
                    tabIndex={searchOpen ? 0 : -1}
                  />
                </form>

                {searchOpen && isFocused && searchQuery && suggestions.length > 0 && (
                  <div
                    className="
                      absolute top-full left-0 mt-6 w-[14rem]
                      z-[320]
                      overflow-hidden
                      bg-white/100
                      backdrop-blur-xl ring- ring-[#2b9cb3]/50 rounded-lg shadow-2xl
                    "
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <ul
                      className="max-h-96 overflow-auto divide-y divide-[#2b9cb3]/40 normal-case"
                      role="listbox"
                    >
                      {suggestions.map((s) => (
                        <li key={s.slug} role="option">
                          <Link
                            to={cardPath(s.slug)}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-[#0f3d3f]/5 transition"
                            onClick={() => {
                              setSearchQuery("");
                              setIsFocused(false);
                              setSearchOpen(false);
                            }}
                          >
                            {s.image || s.img || s.thumbnail ? (
                              <img
                                src={s.image || s.img || s.thumbnail}
                                alt={s.name}
                                className="h-7 w-auto rounded"
                                loading="lazy"
                              />
                            ) : (
                              <div className="h-7 w-7 rounded bg-white/10 flex items-center justify-center text-[10px] uppercase text-[white]/80">
                                C
                              </div>
                            )}
                            <span className="text-sm text-[#0f3d3f] font-[arial]">{s.name}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {/* /inline search */}
            </div>
          </div>
        </div>
      </nav>

      {/* =========================
            MOBILE NAVBAR
        ========================= */}

      {/* Hamburger / Close — fixed top layer */}
      <div className="min-[1050px]:hidden absolute top-20 right-4 z-[330]">
        <button
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          aria-label={showMobileMenu ? "Close menu" : "Open menu"}
          className="text-white"
        >
          {showMobileMenu ? <X size={0} /> : <HamburgerIcon isOpen={false} />}
        </button>
      </div>

      {/* Dim overlay (tap to close) */}
      {showMobileMenu && (
        <div
          className="fixed inset-0 bg-black/40 z-[300]"
          onClick={() => setShowMobileMenu(false)}
        />
      )}

      {/* Slide-out panel */}
      {showMobileMenu && (
        <div
          ref={menuRef}
          className="
            fixed top-0 right-0 h-[100dvh] w-[70%] max-w-[420px] z-[320]
            px-5 pt-16 pb-6 overflow-y-auto flex flex-col text-white
            shadow-xl animate-slideInRight font-[arial]
            uppercase tracking-[0.08em] text-[15px] font-medium
            bg-gradient-to-b from-[#004C85]/70 via-[#14656a]/95 to-[#004C85]/55
            backdrop-blur-md border-l border-[#14656a]/90
          "
        >
          {/* Inline close (X) above search bar */}
          <div className="w-full flex justify-end mt-2 mb-4 pr-[8px]">
            <button
              onClick={() => setShowMobileMenu(false)}
              aria-label="Close menu"
              className="text-white"
            >
              <X size={32} />
            </button>
          </div>

          {/* Mobile search with suggestions */}
          <div ref={formRef} className="w-full relative mb-3">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmitSearch(searchQuery);
              }}
              className="flex"
            >
              <div className="relative w-full sm:w-[300px] ml-auto">
                <div
                  className="
                    flex items-center rounded-full px-4 py-2
                    bg-white/5 ring-1 ring-white/15
                    focus-within:ring-2 focus-within:ring-[#2b9cb3]/60
                    shadow-sm overflow-hidden
                  "
                >
                  <input
                    type="text"
                    placeholder="Search Tarot Cards..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 bg-transparent outline-none text-white placeholder-white/60 text-[14px] tracking-normal caret-white"
                  />
                  <button
                    type="submit"
                    className="text-white/80 hover:text-white flex items-center justify-center"
                    aria-label="Search"
                  >
                    <Search size={18} />
                  </button>
                </div>
              </div>
            </form>

            {/* Suggestions dropdown */}
            {suggestions.length > 0 && (
              <div
                className="
                  absolute left-0 top-full mt-2 w-full z-[330]
                  overflow-hidden
                  bg-gradient-to-b from-[#0f3d3f]/90 via-[#14656a]/90 to-[#0f3d3f]/90
                  backdrop-blur-md ring-1 ring-[#2b9cb3]/50 rounded-lg shadow-2xl
                  normal-case pointer-events-auto
                "
                onPointerDownCapture={(e) => e.stopPropagation()}
                onClickCapture={(e) => e.stopPropagation()}
              >
                <ul className="divide-y divide-[#2b9cb3]/30" role="listbox">
                  {suggestions.map((s) => {
                    const to = s.slug
                      ? (s.slug.startsWith("/") ? s.slug : `/tarot-card/${s.slug}`)
                      : "#";
                    return (
                      <li key={s.slug} role="option">
                        <Link
                          to={to}
                          className="flex items-center gap-3 px-3 py-2 hover:bg-white/5 transition"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            goTo(to);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                        >
                          {s.image || s.img || s.thumbnail ? (
                            <img
                              src={s.image || s.img || s.thumbnail}
                              alt={s.name}
                              className="h-7 w-auto rounded"
                              loading="lazy"
                            />
                          ) : (
                            <div className="h-7 w-7 rounded bg-white/10 flex items-center justify-center text-[10px] uppercase text-white/80">
                              C
                            </div>
                          )}
                          <span className="text-sm text-white">{s.name}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          {/* Links & accordions */}
          <Link
            to="/"
            onClick={() => setShowMobileMenu(false)}
            className="w-full text-right py-3 hover:underline hover:scale-110 transform origin-right"
          >
            HOME
          </Link>

          <Link
            to="/what-is-tarot-cards-complete-guide"
            onClick={() => setShowMobileMenu(false)}
            className="w-full text-right py-3 hover:underline hover:scale-110 transform origin-right"
          >
            WHAT'S TAROT
          </Link>

          {/* Tarot Cards accordion */}
          <div className="w-full text-right py-3 border-t border-[#e6dff5]">
            <button
              onClick={() => setShowMobileCards(!showMobileCards)}
              className="w-full flex justify-end items-center gap-2 hover:underline hover:scale-110 transform origin-right"
            >
              TAROT CARDS
              <ChevronDown
                size={18}
                className={`transition-transform ${showMobileCards ? "rotate-180" : ""}`}
              />
            </button>

            {showMobileCards && (
              <ul className="mt-2 space-y-1 text-right normal-case font-normal tracking-normal">
                {tarotSuits.map((item) => (
                  <li key={item.slug}>
                    <Link
                      to={item.slug}
                      onClick={() => setShowMobileMenu(false)}
                      className="block py-1 pr-3 hover:underline hover:scale-110 transform origin-right"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Emotions accordion */}
          <div className="w-full text-right py-3 border-t border-[#e6dff5]">
            <button
              onClick={() => setShowMobileEmotions(!showMobileEmotions)}
              className="w-full flex justify-end items-center gap-2 hover:underline hover:scale-110 transform origin-right"
            >
              EMOTIONS
              <ChevronDown
                size={18}
                className={`transition-transform ${showMobileEmotions ? "rotate-180" : ""}`}
              />
            </button>

            {showMobileEmotions && (
              <ul className="mt-2 space-y-1 text-right normal-case font-normal tracking-normal">
                {emotionsList.map((emotion) => {
                  const slug = emotion.toLowerCase().replace(/ /g, "-");
                  return (
                    <li key={emotion}>
                      <Link
                        to={`/emotions/tarot-reading-for-${slug}`}
                        onClick={() => setShowMobileMenu(false)}
                        className="block py-1 pr-3 hover:underline hover:scale-110 transform origin-right"
                      >
                        {emotion}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* NEW: Yes/No + Read My Card (before Fate Friday) */}
          <Link
            to="/yes-no-tarot"
            onClick={() => setShowMobileMenu(false)}
            className="w-full text-right py-3 border-t border-[#e6dff5] hover:underline hover:scale-110 transform origin-right"
          >
            YES/NO TAROT
          </Link>

          <Link
            to="/readmycard"
            onClick={() => setShowMobileMenu(false)}
            className="w-full text-right py-3 hover:underline hover:scale-110 transform origin-right"
          >
            READ MY CARD
          </Link>

          {/* NEW: Tarotscope accordion */}
          <div className="w-full text-right py-3 border-t border-[#e6dff5]">
            <button
              onClick={() => setShowMobileTarotscope(!showMobileTarotscope)}
              className="w-full flex justify-end items-center gap-2 hover:underline hover:scale-110 transform origin-right"
            >
              TAROTSCOPE
              <ChevronDown
                size={18}
                className={`transition-transform ${showMobileTarotscope ? "rotate-180" : ""}`}
              />
            </button>

            {showMobileTarotscope && (
              <ul className="mt-2 space-y-1 text-right normal-case font-normal tracking-normal">
                {/* Optional: All signs grid page */}
                <li key="all-signs">
                  <Link
                    to="/tarotscope"
                    onClick={() => setShowMobileMenu(false)}
                    className="block py-1 pr-3 hover:underline hover:scale-110 transform origin-right"
                  >
                    All Signs
                  </Link>
                </li>
                {ZODIAC_SIGNS.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/tarotscope/${s.slug}`}
                      onClick={() => setShowMobileMenu(false)}
                      className="block py-1 pr-3 hover:underline hover:scale-110 transform origin-right"
                    >
                      {s.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Link
            to="/fate-friday"
            onClick={() => setShowMobileMenu(false)}
            className="w-full text-right py-3 border-t border-[#e6dff5] hover:underline hover:scale-110 transform origin-right"
          >
            FATE FRIDAY
          </Link>

          <Link
            to="/full-moon"
            onClick={() => setShowMobileMenu(false)}
            className="w-full text-right py-3 hover:underline hover:scale-110 transform origin-right"
          >
            FULL MOON
          </Link>

          {/* This Space accordion */}
          <div className="w-full text-right py-3 border-t border-[#e6dff5]">
            <button
              onClick={() => setShowMobileSiteInfo(!showMobileSiteInfo)}
              className="w-full flex justify-end items-center gap-2 hover:underline hover:scale-110 transform origin-right"
            >
              THIS SPACE
              <ChevronDown
                size={18}
                className={`transition-transform ${showMobileSiteInfo ? "rotate-180" : ""}`}
              />
            </button>

            {showMobileSiteInfo && (
              <ul className="mt-2 space-y-1 text-right normal-case font-normal tracking-normal">
                <li>
                  <Link
                    to="/about"
                    onClick={() => setShowMobileMenu(false)}
                    className="block py-1 pr-3 hover:underline hover:scale-110 transform origin-right"
                  >
                    About
                  </Link>
                </li>
                <li>
                  <Link
                    to="/disclaimer"
                    onClick={() => setShowMobileMenu(false)}
                    className="block py-1 pr-3 hover:underline hover:scale-110 transform origin-right"
                  >
                    Disclaimer
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    onClick={() => setShowMobileMenu(false)}
                    className="block py-1 pr-3 hover:underline hover:scale-110 transform origin-right"
                  >
                    Privacy
                  </Link>
                </li>
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}
